/**
 * HTTP client for the callable router Lambda, replacing firebase/functions'
 * httpsCallable. It speaks the same wire format the Firebase callables did:
 *
 *   POST <apiUrl>/<name>   {"data": ...}   Authorization: Bearer <id token>
 *   → {"result": ...}  or  {"error": {"status": "PERMISSION_DENIED", "message", "details"?}}
 *
 * and keeps httpsCallable's call shape — `fn(data)` resolving to `{ data }` —
 * and its error shape — `code` like `functions/permission-denied` — so call
 * sites did not change.
 */

/** Firebase's default callable timeout. */
export const DEFAULT_TIMEOUT_MS = 70_000;

export class CallableError extends Error {
  constructor(
    /** e.g. `functions/permission-denied`. */
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'CallableError';
  }
}

/** `PERMISSION_DENIED` → `functions/permission-denied`. */
export function codeFromStatus(status: string): string {
  return `functions/${status.toLowerCase().replace(/_/g, '-')}`;
}

/** Fallback when the body carries no error status, as Firebase's client does. */
export function codeFromHttpStatus(httpStatus: number): string {
  const map: Record<number, string> = {
    400: 'invalid-argument',
    401: 'unauthenticated',
    403: 'permission-denied',
    404: 'not-found',
    409: 'aborted',
    429: 'resource-exhausted',
    499: 'cancelled',
    500: 'internal',
    501: 'unimplemented',
    503: 'unavailable',
    504: 'deadline-exceeded',
  };
  return `functions/${map[httpStatus] ?? 'unknown'}`;
}

/** Turns an HTTP status + parsed body into the result, or throws CallableError. */
export function parseCallableResponse<Res>(httpStatus: number, body: unknown): Res {
  const obj = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const error = obj['error'] as
    | { status?: unknown; message?: unknown; details?: unknown }
    | undefined;

  if (httpStatus >= 200 && httpStatus < 300 && !error) {
    if (!('result' in obj)) {
      throw new CallableError('functions/internal', 'Response is missing data field.');
    }
    return obj['result'] as Res;
  }

  const code =
    error && typeof error.status === 'string'
      ? codeFromStatus(error.status)
      : codeFromHttpStatus(httpStatus);
  const message =
    error && typeof error.message === 'string' ? error.message : code.replace('functions/', '');
  throw new CallableError(code, message, error?.details);
}

export interface CallableTransport {
  apiUrl: () => string;
  getToken: () => Promise<string | null>;
  renewToken: () => Promise<string | null>;
  fetch: typeof fetch;
}

export interface CallableResult<Res> {
  data: Res;
}

export type CallableFn<Req, Res> = (data?: Req | null) => Promise<CallableResult<Res>>;

export function makeCallable<Req, Res>(
  transport: CallableTransport,
  name: string,
  opts: { timeout?: number } = {},
): CallableFn<Req, Res> {
  const timeout = opts.timeout ?? DEFAULT_TIMEOUT_MS;

  async function send(data: Req | null, token: string | null): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      return await transport.fetch(`${transport.apiUrl()}/${name}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ data }),
        signal: controller.signal,
      });
    } catch (err) {
      if (controller.signal.aborted) {
        throw new CallableError('functions/deadline-exceeded', 'deadline-exceeded');
      }
      throw new CallableError(
        'functions/unavailable',
        err instanceof Error ? err.message : 'Network error',
      );
    } finally {
      clearTimeout(timer);
    }
  }

  return async (data) => {
    let res = await send(data ?? null, await transport.getToken());
    if (res.status === 401) {
      // Expired or revoked token: renew once and retry.
      const renewed = await transport.renewToken();
      if (renewed) res = await send(data ?? null, renewed);
    }
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    return { data: parseCallableResponse<Res>(res.status, body) };
  };
}
